import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { chunk, cloneDeep, head, isNil, sumBy } from 'lodash';

import { PDF_DEFAULT_USE_FONT, PDF_INITIAL_HORIZONTAL_MARGIN_XCAT } from '@config/global-constants';
import {
    PDF_FONTSIZE_HEADER,
    PDF_FONTSIZE_NORMAL,
    PDF_FONTSIZE_SUB_TITLE,
    PDF_FONTSIZE_TABLE_TITLE,
    PDF_FONTSIZE_TABLE_TOTAL,
    PDF_FONTSIZE_TITLE,
} from '@config/index';
import {
    AdoItems,
    DetailTableHeader,
    PdfTypeEnum,
    QuestionType,
    SuiteInfoType,
    SuiteOrderSummaryData,
    SuiteOrderSummaryOptions,
} from '@shared/types/report';
import { isNullOrUndefined } from '@shared/utils';

@Injectable({
    providedIn: 'root',
})
export class SuiteOrderSummaryHelperService {
    private startY: number = 0;
    private paddingH: number = PDF_INITIAL_HORIZONTAL_MARGIN_XCAT;
    private maxLineWidth: number = 180;
    private warningIconBase64 =
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAEeSURBVHgB3ZNBUsJAEEXfjLAmLlwIm3gDvAHewBuAJxBPoDfRG3AEvYHegFjlwrKwzBpJYo+VlDDpMAIr+IvUpOf3rz/ze+AgMKM3/KA3+g/XhAhfxHHG92MBUYv22TFJuo5vQ4IiditisSyjhaxDfBNyJyJTr+XihLcntnEoYpN6tVjrslGwDKGvbA1mdC+b+hqPLMlOy7sTkr2CHPm/L5uSI9rnWkCqw0+6VRAq3N6cxVjbqwm6IHLMHQFYimvHrdc9ZMpoGIqOwXS8cqRzlyBB9KXwzEZYHSPr2Z1oLXJnEgo3qp43RnbJ3agpCDluIt8XdAzeOR3/cVl5rzHbIa3e+a/DnPlwBzGHqBqjlvtkmFc5/QM7wJKn7AV+AF1jWCwPKQPjAAAAAElFTkSuQmCC';

    constructor(private translations: TranslateService) {}

    public drawPdf(
        doc: jsPDF,
        headers: { companyName: string; reportRun: string },
        title: string,
        subTitles: SuiteInfoType[],
        data: SuiteOrderSummaryData[],
    ): void {
        this.startY = 22;

        this.drawTitles(doc, title, subTitles);

        data.forEach((el, idx) => {
            if (idx) {
                doc.addPage();
                this.startY = 22;
                this.drawTitles(doc, title, subTitles);
            }

            Reflect.ownKeys(el).forEach((key) => {
                this.drawPdfDataByType(doc, el[key]);
            });
        });

        this.addPageHeaders(doc, headers);
    }

    private drawTitles(doc: jsPDF, title: string, subTitles: SuiteInfoType[]) {
        doc.setFont(PDF_DEFAULT_USE_FONT, 'normal');
        doc.setFontSize(PDF_FONTSIZE_TITLE);
        doc.text(title, this.paddingH, this.startY);
        this.startY += 4;

        doc.setFontSize(PDF_FONTSIZE_SUB_TITLE);
        subTitles?.forEach((sub) => {
            this.startY += 2;
            doc.text(`${sub.name}: ${sub.value}`, this.paddingH, this.startY);
            this.startY += 3;
        });
    }

    private addPageHeaders(doc: jsPDF, headers: { companyName: string; reportRun: string }) {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFont(PDF_DEFAULT_USE_FONT, 'normal');
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(PDF_FONTSIZE_HEADER);
            doc.text(headers.companyName, this.paddingH, 10);
            doc.text(headers.reportRun, pageWidth - this.paddingH, 10, { align: 'right' });
        }
    }

    private drawPdfDataByType(doc: jsPDF, row: SuiteOrderSummaryOptions) {
        if (!row.show) {
            return;
        }

        switch (row.pdfType) {
            case PdfTypeEnum.columns:
                this.drawColumns(doc, row);
                break;
            case PdfTypeEnum.table:
                this.drawTable(doc, row);
                break;
            case PdfTypeEnum.list:
            case PdfTypeEnum.orderInfoList:
                this.drawList(doc, row, row.pdfType);
                break;
            case PdfTypeEnum.notes:
                this.drawNotes(doc, row);
                break;
        }
    }

    private drawColumns(doc: jsPDF, rows: SuiteOrderSummaryOptions) {
        if (!rows?.list?.length) {
            return;
        }

        const rowsCopy = cloneDeep(rows);
        const suiteInfoList = rowsCopy.list as Array<SuiteInfoType>;
        const suiteHead = suiteInfoList.shift();

        const warningStates = ['voided', 'voided-post-payment'];
        const isWarningOrder = warningStates.includes(rows.order_state);
        const orderNumberText = `${suiteHead.name}: ${suiteHead.value}`;

        const displayTexts = {
            voided: 'Voided',
            'voided-post-payment': 'Voided Post Payment',
        };
        const displayText = displayTexts[rows.order_state] || rows.order_state;

        this.startY += 10;
        doc.setFont(PDF_DEFAULT_USE_FONT, 'bold');
        doc.setFontSize(PDF_FONTSIZE_TABLE_TITLE);
        doc.text(orderNumberText, this.paddingH, this.startY);

        if (isWarningOrder) {
            const textWidth = doc.getTextWidth(orderNumberText);
            doc.addImage(this.warningIconBase64, 'PNG', this.paddingH + textWidth + 2, this.startY - 4, 5, 5);
            doc.setTextColor(255, 0, 0);
            doc.text(displayText, this.paddingH + textWidth + 9, this.startY);
            doc.setTextColor(0, 0, 0);
        }

        this.startY += 2;
        doc.setFont(PDF_DEFAULT_USE_FONT, 'normal');
        doc.setFontSize(PDF_FONTSIZE_SUB_TITLE);

        const pageWidth = doc.internal.pageSize.getWidth();
        const col1X = this.paddingH;
        const col2X = this.paddingH + (pageWidth - 2 * this.paddingH) * 0.5;
        const col1Width = col2X - col1X - 2;
        const col2Width = pageWidth - this.paddingH - col2X;
        const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;

        const pairs = chunk(suiteInfoList, 2);
        pairs.forEach((pair) => {
            this.startY += 6;
            const text1 = `${pair[0].name}: ${pair[0].value}`;
            const lines1 = doc.splitTextToSize(text1, col1Width);
            doc.text(lines1, col1X, this.startY);

            let lines2: string[] = [];
            if (pair[1]) {
                const text2 = `${pair[1].name}: ${pair[1].value}`;
                lines2 = doc.splitTextToSize(text2, col2Width);
                doc.text(lines2, col2X, this.startY);
            }

            const maxLines = Math.max(lines1.length, lines2.length);
            if (maxLines > 1) {
                this.startY += (maxLines - 1) * lineHeight;
            }
        });
    }

    private drawTable(doc: jsPDF, rows: SuiteOrderSummaryOptions) {
        const titleText = isNil(rows.PdfTitleSuffix) ? rows.title || '' : (rows.title || '') + rows.PdfTitleSuffix;

        if (titleText) {
            this.startY += 10;
            doc.setFont(PDF_DEFAULT_USE_FONT, 'normal');
            doc.setFontSize(PDF_FONTSIZE_TABLE_TITLE);
            doc.text(titleText, this.paddingH, this.startY);
            this.startY += 3;
        } else {
            this.startY += 5;
        }

        const headers = rows.header?.filter((el) => !el.hidePdf);
        if (!headers?.length) {
            return;
        }
        const widthPercentages = this.getExportedColumnsWidths(headers, rows.columnAverage);
        const tableWidth = doc.internal.pageSize.getWidth() - 2 * this.paddingH;
        const colWidths: { [key: number]: { cellWidth: number } } = {};
        const cellWidthsMm: number[] = [];
        widthPercentages.forEach((w, i) => {
            const cw = (parseFloat(w) / 100) * tableWidth;
            colWidths[i] = { cellWidth: cw };
            cellWidthsMm.push(cw);
        });

        const head = [
            headers.map((th, i) => ({
                content: th.headerName,
                styles: {
                    fontStyle: 'bold' as const,
                    fontSize: PDF_FONTSIZE_NORMAL,
                    halign: (th?.cellClass?.includes('align-right') ? 'right' : 'left') as 'right' | 'left',
                    cellPadding: { top: 2.5, bottom: 2.5, left: 1.5, right: 1.5 },
                    cellWidth: cellWidthsMm[i],
                },
            })),
        ];

        const body = rows.list?.length
            ? (<Array<AdoItems>>rows.list).reduce((result, tr, trIdx, arr) => {
                  const isLastRowNoFoot = !rows.total && trIdx === arr.length - 1;
                  const baseFillColor = isLastRowNoFoot
                      ? ([225, 225, 225] as [number, number, number])
                      : trIdx % 2 === 0
                        ? ([246, 246, 246] as [number, number, number])
                        : ([255, 255, 255] as [number, number, number]);

                  const dataRow = headers.map((th) => {
                      const showValue = tr[th.field]?.value ?? tr[th.field];
                      const content =
                          th.field === 'item_name' && th.headerName === 'Item Name' && tr.level
                              ? `- ${showValue}`
                              : (showValue ?? '');
                      return {
                          content: String(content),
                          styles: {
                              fontSize: PDF_FONTSIZE_NORMAL,
                              halign: (th?.cellClass?.includes('align-right') ? 'right' : 'left') as 'right' | 'left',
                              fontStyle: (th?.cellClass?.includes('font-bold') ? 'bold' : 'normal') as
                                  | 'bold'
                                  | 'normal',
                              textColor: tr[th.field]?.isNegative
                                  ? ([231, 76, 60] as [number, number, number])
                                  : ([0, 0, 0] as [number, number, number]),
                              fillColor: baseFillColor,
                              ...(th.field === 'item_name' && th.headerName === 'Item Name' && tr.level
                                  ? { cellPadding: { left: tr.level * 1.5, top: 2.5, bottom: 2.5, right: 1.5 } }
                                  : {}),
                          },
                      };
                  });

                  result.push(dataRow);

                  if (!isNil(tr.comment) && `${tr.comment}`.trim() !== '') {
                      result.push([
                          {
                              content: `${this.translations.instant('COMMENT')}: ${tr.comment}`,
                              colSpan: headers.length,
                              styles: {
                                  fontSize: PDF_FONTSIZE_NORMAL,
                                  halign: 'left' as const,
                                  fillColor: baseFillColor,
                              },
                          },
                      ]);
                  }

                  return result;
              }, [])
            : [];

        const foot = rows.total
            ? [
                  headers.map((th, idx) => ({
                      content: idx === 0 ? 'Total' : String(rows.total[th.field] ?? ''),
                      styles: {
                          fontStyle: 'normal' as const,
                          fontSize: PDF_FONTSIZE_TABLE_TOTAL,
                          halign: (th?.cellClass?.includes('align-right') ? 'right' : 'left') as 'right' | 'left',
                          cellWidth: cellWidthsMm[idx],
                      },
                  })),
              ]
            : undefined;

        autoTable(doc, {
            startY: this.startY,
            head,
            body,
            foot,
            theme: 'plain',
            rowPageBreak: 'avoid',
            tableWidth: tableWidth,
            styles: {
                fontSize: PDF_FONTSIZE_NORMAL,
                font: PDF_DEFAULT_USE_FONT,
                cellPadding: { top: 2.5, bottom: 2.5, left: 1.5, right: 1.5 },
                minCellHeight: 9,
                valign: 'middle',
            },
            columnStyles: colWidths,
            margin: { left: this.paddingH, right: this.paddingH },
            footStyles: {
                fillColor: [225, 225, 225],
            },
            alternateRowStyles: {
                fillColor: [246, 246, 246],
            },
            didDrawCell: (data) => {
                doc.setDrawColor(204, 204, 204);
                doc.setLineWidth(0.2);
                doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
                doc.line(
                    data.cell.x,
                    data.cell.y + data.cell.height,
                    data.cell.x + data.cell.width,
                    data.cell.y + data.cell.height,
                );
            },
        });
        this.startY = (doc as any).lastAutoTable.finalY;
    }

    private drawList(doc: jsPDF, rows: SuiteOrderSummaryOptions, type: string) {
        if (type === PdfTypeEnum.orderInfoList) {
            this.startY += 10;
        }

        if (!rows.list?.length) {
            return;
        }

        const listData = rows.list as Array<SuiteInfoType>;
        const body = listData.map((row, idx, arr) => {
            const isLastTwo = (idx === arr.length - 1 || idx === arr.length - 2) && type === PdfTypeEnum.orderInfoList;
            if (row.class === 'info-title') {
                return [
                    {
                        content: row?.name || '',
                        colSpan: 3,
                        styles: {
                            fontSize: PDF_FONTSIZE_TABLE_TOTAL,
                            fillColor: [247, 247, 247] as [number, number, number],
                        },
                    },
                ];
            }
            return [
                {
                    content: '',
                    styles: { fillColor: (isLastTwo ? [225, 225, 225] : [247, 247, 247]) as [number, number, number] },
                },
                {
                    content: row?.name || '',
                    styles: {
                        fontSize: PDF_FONTSIZE_TABLE_TOTAL,
                        fillColor: (isLastTwo ? [225, 225, 225] : [247, 247, 247]) as [number, number, number],
                    },
                },
                {
                    content: String(row?.value || ''),
                    styles: {
                        halign: 'right' as const,
                        fontSize: PDF_FONTSIZE_TABLE_TOTAL,
                        textColor: (row.isNegative ? [231, 76, 60] : [0, 0, 0]) as [number, number, number],
                        fillColor: (isLastTwo ? [225, 225, 225] : [247, 247, 247]) as [number, number, number],
                    },
                },
            ];
        });

        const tableWidth = doc.internal.pageSize.getWidth() - 2 * this.paddingH;
        autoTable(doc, {
            startY: this.startY,
            body,
            theme: 'plain',
            rowPageBreak: 'avoid',
            styles: {
                fontSize: PDF_FONTSIZE_TABLE_TOTAL,
                font: PDF_DEFAULT_USE_FONT,
                cellPadding: { top: 2.5, bottom: 2.5, left: 1.5, right: 1.5 },
            },
            columnStyles: {
                0: { cellWidth: tableWidth * 0.48 },
                1: { cellWidth: tableWidth * 0.26 },
                2: { cellWidth: tableWidth * 0.26 },
            },
            margin: { left: this.paddingH, right: this.paddingH },
            didDrawCell: (data) => {
                doc.setDrawColor(204, 204, 204);
                doc.setLineWidth(0.2);
                doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
                doc.line(
                    data.cell.x,
                    data.cell.y + data.cell.height,
                    data.cell.x + data.cell.width,
                    data.cell.y + data.cell.height,
                );
            },
        });
        this.startY = (doc as any).lastAutoTable.finalY;
    }

    private drawNotes(doc: jsPDF, rows: SuiteOrderSummaryOptions) {
        this.startY += 10;
        doc.setFont(PDF_DEFAULT_USE_FONT, 'normal');
        doc.setFontSize(PDF_FONTSIZE_TABLE_TITLE);
        doc.text(rows.title || '', this.paddingH, this.startY);
        this.startY += 3;

        const notesList = rows.list as Array<SuiteInfoType>;

        const notesBody = notesList
            .filter((note) => !['question', 'allergens'].includes(note.id))
            .reduce<any[][]>((body, currentNote, idx) => {
                return [
                    ...body,
                    [
                        {
                            content: `${currentNote.name}:`,
                            styles: {
                                fontStyle: 'bold' as const,
                                fontSize: PDF_FONTSIZE_NORMAL,
                                fillColor: idx % 2 ? ([246, 246, 246] as number[]) : null,
                                lineWidth: { top: 0.2, bottom: 0, left: 0, right: 0 },
                                lineColor: [204, 204, 204] as number[],
                                cellPadding: { left: 3, top: 1.5, bottom: 1.5, right: 1.5 },
                            },
                        },
                    ],
                    [
                        {
                            content: currentNote.value || '',
                            styles: {
                                fontSize: PDF_FONTSIZE_NORMAL,
                                fillColor: idx % 2 ? ([246, 246, 246] as number[]) : null,
                                lineWidth: 0,
                                cellPadding: { left: 3, top: 1.5, bottom: 4, right: 1.5 },
                            },
                        },
                    ],
                ];
            }, []);

        if (notesBody.length) {
            autoTable(doc, {
                startY: this.startY,
                body: notesBody,
                theme: 'plain',
                styles: {
                    fontSize: PDF_FONTSIZE_NORMAL,
                    font: PDF_DEFAULT_USE_FONT,
                },
                margin: { left: this.paddingH, right: this.paddingH },
                didDrawCell: (data) => {
                    if (data.row.index === data.table.body.length - 1) {
                        doc.setDrawColor(204, 204, 204);
                        doc.setLineWidth(0.2);
                        doc.line(
                            data.cell.x,
                            data.cell.y + data.cell.height,
                            data.cell.x + data.cell.width,
                            data.cell.y + data.cell.height,
                        );
                    }
                },
            });
            this.startY = (doc as any).lastAutoTable.finalY;
        }

        const question = head(notesList.filter((note) => note.id === 'question'));
        if (question?.value?.length) {
            const questionBody: any[][] = [
                [
                    {
                        content: `${question.name}:`,
                        styles: {
                            fontStyle: 'bold' as const,
                            fontSize: PDF_FONTSIZE_NORMAL,
                            lineWidth: { top: 0.2, bottom: 0.2, left: 0, right: 0 },
                            lineColor: [204, 204, 204] as number[],
                            cellPadding: { left: 3, top: 1.5, bottom: 1.5, right: 1.5 },
                        },
                    },
                ],
            ];

            (question.value as Array<QuestionType>).forEach((q, idx) => {
                const bgColor = idx % 2 === 0 ? ([246, 246, 246] as number[]) : null;
                questionBody.push(
                    [
                        {
                            content: q.questionIndexDes || '',
                            styles: {
                                fontSize: PDF_FONTSIZE_NORMAL,
                                fillColor: bgColor,
                                lineWidth: 0,
                                cellPadding: { left: 3, top: 1.5, bottom: 1.5, right: 1.5 },
                            },
                        },
                    ],
                    [
                        {
                            content: q.answer || '',
                            styles: {
                                fontSize: PDF_FONTSIZE_NORMAL,
                                fillColor: bgColor,
                                lineWidth: 0,
                                cellPadding: { left: 3, top: 1.5, bottom: 4, right: 1.5 },
                            },
                        },
                    ],
                );
            });

            autoTable(doc, {
                startY: this.startY,
                body: questionBody,
                theme: 'plain',
                styles: {
                    fontSize: PDF_FONTSIZE_NORMAL,
                    font: PDF_DEFAULT_USE_FONT,
                },
                margin: { left: this.paddingH, right: this.paddingH },
            });
            this.startY = (doc as any).lastAutoTable.finalY;
        }

        const allergens = head(notesList.filter((note) => note.id === 'allergens'));
        if (allergens) {
            const allergensBody: any[][] = [
                [
                    {
                        content: `${allergens.name}:`,
                        styles: {
                            fontStyle: 'bold' as const,
                            fontSize: PDF_FONTSIZE_NORMAL,
                            lineWidth: { top: 0.2, bottom: 0, left: 0, right: 0 },
                            lineColor: [204, 204, 204] as number[],
                            cellPadding: { left: 3, top: 1.5, bottom: 1.5, right: 1.5 },
                        },
                    },
                ],
                [
                    {
                        content: allergens.value || '',
                        styles: {
                            fontSize: PDF_FONTSIZE_NORMAL,
                            lineWidth: 0,
                            cellPadding: { left: 3, top: 1.5, bottom: 4, right: 1.5 },
                        },
                    },
                ],
            ];

            autoTable(doc, {
                startY: this.startY,
                body: allergensBody,
                theme: 'plain',
                styles: {
                    fontSize: PDF_FONTSIZE_NORMAL,
                    font: PDF_DEFAULT_USE_FONT,
                },
                margin: { left: this.paddingH, right: this.paddingH },
                didDrawCell: (data) => {
                    if (data.row.index === data.table.body.length - 1) {
                        doc.setDrawColor(204, 204, 204);
                        doc.setLineWidth(0.2);
                        doc.line(
                            data.cell.x,
                            data.cell.y + data.cell.height,
                            data.cell.x + data.cell.width,
                            data.cell.y + data.cell.height,
                        );
                    }
                },
            });
            this.startY = (doc as any).lastAutoTable.finalY;
        }
    }

    private getExportedColumnsWidths(columnsToExport: DetailTableHeader[], columnAverage?: boolean): string[] {
        if (columnAverage) {
            return columnsToExport.map(() => 100 / columnsToExport.length + '%');
        } else {
            const totalWidth = sumBy(columnsToExport, 'width');
            const count = columnsToExport.filter(({ width }) => isNullOrUndefined(width)).length;
            if (count === 0) {
                return columnsToExport.map(({ width }) => (width / totalWidth) * 100 + '%');
            }
            return columnsToExport.map(({ width }) => {
                if (isNullOrUndefined(width)) {
                    return (100 - totalWidth) / count + '%';
                } else {
                    return width + '%';
                }
            });
        }
    }
}
